// src/pages/master/DepartmentPage.js
import React, { useEffect, useMemo, useState } from "react";

const DepartmentPage = () => {
    const token = localStorage.getItem("token");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    // create form
    const [departmentCode, setDepartmentCode] = useState("");
    const [departmentName, setDepartmentName] = useState("");
    const [createdBy, setCreatedBy] = useState("ADMIN");
    const [status, setStatus] = useState(true);

    // edit modal/form
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [editStatus, setEditStatus] = useState(true);

    // view by id
    const [viewId, setViewId] = useState("");
    const [viewData, setViewData] = useState(null);

    // ui helpers
    const [search, setSearch] = useState("");
    const [msg, setMsg] = useState({ type: "", text: "" });

    const baseUrl = "https://mis.empowerplus.in/api/departments";

    const headersAuth = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );

    const setFlash = (type, text) => {
        setMsg({ type, text });
    };

    const safeJson = async (res) => {
        const text = await res.text();
        try {
            return text ? JSON.parse(text) : null;
        } catch {
            return text;
        }
    };

    // ✅ 2) GET ALL
    const fetchAll = async () => {
        setLoading(true);
        try {
            const res = await fetch(baseUrl, { headers: headersAuth });
            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Failed (${res.status})`);
            }
            const data = await safeJson(res);
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setFlash("err", e.message || "Failed to load departments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ✅ 1) CREATE
    const createDepartment = async (e) => {
        e.preventDefault();

        if (!departmentCode.trim() || !departmentName.trim()) {
            setFlash("err", "Department Code and Name are required");
            return;
        }

        try {
            const payload = {
                departmentCode: departmentCode.trim(),
                departmentName: departmentName.trim(),
                createdBy: createdBy.trim() || "ADMIN",
                status: !!status,
            };

            const res = await fetch(baseUrl, {
                method: "POST",
                headers: headersAuth,
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Create failed (${res.status})`);
            }

            setFlash("ok", "Department created");
            setDepartmentCode("");
            setDepartmentName("");
            setCreatedBy("ADMIN");
            setStatus(true);
            fetchAll();
        } catch (e2) {
            setFlash("err", e2.message || "Create failed");
        }
    };

    // ✅ 4) GET BY ID
    const getById = async () => {
        if (!viewId) return setFlash("err", "Enter an ID to fetch");

        setViewData(null);
        try {
            const res = await fetch(`${baseUrl}/${viewId}`, { headers: headersAuth });
            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Not found (${res.status})`);
            }
            const data = await safeJson(res);
            setViewData(data);
            setFlash("ok", `Fetched department #${viewId}`);
        } catch (e) {
            setFlash("err", e.message || "Fetch by ID failed");
        }
    };

    // ✅ 3) UPDATE
    const openEdit = (row) => {
        setEditingId(row.id);
        setEditName(row.departmentName || "");
        setEditStatus(!!row.status);
    };

    const closeEdit = () => {
        setEditingId(null);
        setEditName("");
        setEditStatus(true);
    };

    const updateDepartment = async () => {
        if (!editingId) return;
        if (!editName.trim()) {
            setFlash("err", "Department Name is required");
            return;
        }

        try {
            const payload = {
                departmentName: editName.trim(),
                status: !!editStatus,
            };

            const res = await fetch(`${baseUrl}/${editingId}`, {
                method: "PUT",
                headers: headersAuth,
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Update failed (${res.status})`);
            }

            setFlash("ok", "Department updated");
            closeEdit();
            fetchAll();
        } catch (e) {
            setFlash("err", e.message || "Update failed");
        }
    };

    // ✅ 5) DELETE
    const deleteDepartment = async (id) => {
        const ok = window.confirm(`Delete department #${id}?`);
        if (!ok) return;

        try {
            const res = await fetch(`${baseUrl}/${id}`, {
                method: "DELETE",
                headers: headersAuth,
            });

            if (!res.ok) {
                const err = await safeJson(res);
                throw new Error(err?.message || err || `Delete failed (${res.status})`);
            }

            setFlash("ok", "Department deleted");
            if (String(viewId) === String(id)) setViewData(null);
            fetchAll();
        } catch (e) {
            setFlash("err", e.message || "Delete failed");
        }
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const code = (r.departmentCode || "").toLowerCase();
            const name = (r.departmentName || "").toLowerCase();
            return code.includes(q) || name.includes(q);
        });
    }, [rows, search]);

    return (
        <div style={styles.page}>
            <div style={styles.headerRow}>
                <div>
                    <div style={styles.title}>Department Master</div>
                    <div style={styles.subTitle}>Create, View, Update, Delete</div>
                </div>

                <button style={styles.refreshBtn} onClick={fetchAll} disabled={loading}>
                    {loading ? "Refreshing..." : "Refresh"}
                </button>
            </div>

            {msg.text ? (
                <div
                    style={{
                        ...styles.flash,
                        ...(msg.type === "ok" ? styles.flashOk : styles.flashErr),
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}
                >
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            ) : null}

            {/* CREATE */}
            <div style={styles.card}>
                <div style={styles.cardTitle}>Create Department</div>

                <form onSubmit={createDepartment} style={styles.formGrid}>
                    <div style={styles.field}>
                        <label style={styles.label}>Department Code *</label>
                        <input
                            style={styles.input}
                            value={departmentCode}
                            onChange={(e) => setDepartmentCode(e.target.value)}
                            placeholder="e.g. IT"
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Department Name *</label>
                        <input
                            style={styles.input}
                            value={departmentName}
                            onChange={(e) => setDepartmentName(e.target.value)}
                            placeholder="e.g. Information Technology"
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Created By</label>
                        <input
                            style={styles.input}
                            value={createdBy}
                            onChange={(e) => setCreatedBy(e.target.value)}
                            placeholder="ADMIN"
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Status</label>
                        <div style={styles.toggleRow}>
                            <button
                                type="button"
                                onClick={() => setStatus(true)}
                                style={{
                                    ...styles.pill,
                                    ...(status ? styles.pillOn : styles.pillOff),
                                }}
                            >
                                Active
                            </button>
                            <button
                                type="button"
                                onClick={() => setStatus(false)}
                                style={{
                                    ...styles.pill,
                                    ...(!status ? styles.pillOn : styles.pillOff),
                                }}
                            >
                                Inactive
                            </button>
                        </div>
                    </div>

                    <div style={{ ...styles.field, alignItems: "flex-end" }}>
                        <button type="submit" style={styles.primaryBtn}>
                            Create
                        </button>
                    </div>
                </form>
            </div>

            {/* GET BY ID */}
            {/* <div style={styles.card}>
                <div style={styles.cardTitle}>Get Department By ID</div>

                <div style={styles.row}>
                    <input
                        style={{ ...styles.input, maxWidth: 220 }}
                        value={viewId}
                        onChange={(e) => setViewId(e.target.value)}
                        placeholder="Enter ID (e.g. 1)"
                    />
                    <button style={styles.primaryBtn} onClick={getById}>
                        Fetch
                    </button>
                    <button
                        style={styles.secondaryBtn}
                        onClick={() => {
                            setViewData(null);
                            setViewId("");
                        }}
                    >
                        Clear
                    </button>
                </div>

                {viewData ? (
                    <div style={styles.viewBox}>
                        <div style={styles.kv}>
                            <span style={styles.k}>ID</span>
                            <span style={styles.v}>{viewData.id}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Code</span>
                            <span style={styles.v}>{viewData.departmentCode}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Name</span>
                            <span style={styles.v}>{viewData.departmentName}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Created By</span>
                            <span style={styles.v}>{viewData.createdBy}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Created Date</span>
                            <span style={styles.v}>{viewData.createdDate || "-"}</span>
                        </div>
                        <div style={styles.kv}>
                            <span style={styles.k}>Status</span>
                            <span style={styles.v}>
                                {viewData.status ? "Active" : "Inactive"}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div style={styles.muted}>No record loaded</div>
                )}
            </div> */}

            {/* LIST */}
            <div style={styles.card}>
                <div style={styles.cardTop}>
                    <div style={styles.cardTitle}>All Departments</div>
                    <input
                        style={{ ...styles.input, maxWidth: 300 }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by code or name..."
                    />
                </div>

                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>ID</th>
                                <th style={styles.th}>Code</th>
                                <th style={styles.th}>Name</th>
                                <th style={styles.th}>Created By</th>
                                <th style={styles.th}>Created Date</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td style={styles.td} colSpan={7}>
                                        {loading ? "Loading..." : "No departments found"}
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((r) => (
                                    <tr key={r.id}>
                                        <td style={styles.td}>{r.id}</td>
                                        <td style={styles.td}>{r.departmentCode}</td>
                                        <td style={styles.td}>{r.departmentName}</td>
                                        <td style={styles.td}>{r.createdBy || "-"}</td>
                                        <td style={styles.td}>{r.createdDate || "-"}</td>
                                        <td style={styles.td}>
                                            <span
                                                style={{
                                                    ...styles.badge,
                                                    ...(r.status ? styles.badgeOn : styles.badgeOff),
                                                }}
                                            >
                                                {r.status ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {/* <button
                                                style={styles.smallBtn}
                                                onClick={() => openEdit(r)}
                                            >
                                                Edit
                                            </button> */}
                                            <button
                                                style={{ ...styles.smallBtn, ...styles.dangerBtn }}
                                                onClick={() => deleteDepartment(r.id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* EDIT MODAL */}
            {editingId ? (
                <div style={styles.modalOverlay} onMouseDown={closeEdit}>
                    <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
                        <div style={styles.modalTitle}>Update Department</div>

                        <div style={styles.field}>
                            <label style={styles.label}>Department Name *</label>
                            <input
                                style={styles.input}
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder="e.g. IT & Systems"
                            />
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label}>Status</label>
                            <div style={styles.toggleRow}>
                                <button
                                    type="button"
                                    onClick={() => setEditStatus(true)}
                                    style={{
                                        ...styles.pill,
                                        ...(editStatus ? styles.pillOn : styles.pillOff),
                                    }}
                                >
                                    Active
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditStatus(false)}
                                    style={{
                                        ...styles.pill,
                                        ...(!editStatus ? styles.pillOn : styles.pillOff),
                                    }}
                                >
                                    Inactive
                                </button>
                            </div>
                        </div>

                        <div style={styles.modalActions}>
                            <button style={styles.secondaryBtn} onClick={closeEdit}>
                                Cancel
                            </button>
                            <button style={styles.primaryBtn} onClick={updateDepartment}>
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

const styles = {
    page: {
        padding: 24,
        background: "#f7f7fb",
        minHeight: "100vh",
        fontFamily: "Inter, system-ui, Arial",
        color: "#111827",
    },
    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: 12,
        marginBottom: 14,
    },
    title: { fontSize: 28, fontWeight: 800, letterSpacing: 0.2 },
    subTitle: { marginTop: 4, color: "#6b7280", fontSize: 13 },
    refreshBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
    },
    flash: {
        marginBottom: 14,
        padding: "10px 12px",
        borderRadius: 10,
        fontSize: 14,
    },
    flashOk: { background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" },
    flashErr: { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" },

    card: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        padding: 16,
        marginBottom: 14,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    },
    cardTop: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        marginBottom: 10,
    },
    cardTitle: { fontSize: 16, fontWeight: 800, marginBottom: 10 },

    formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(5, minmax(160px, 1fr))",
        gap: 12,
    },

    field: { display: "flex", flexDirection: "column", gap: 6 },
    label: { fontSize: 12, color: "#6b7280", fontWeight: 700 },

    input: {
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "10px 12px",
        outline: "none",
        fontSize: 14,
        background: "#fff",
    },

    row: { display: "flex", gap: 10, alignItems: "center" },

    toggleRow: { display: "flex", gap: 8 },
    pill: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "8px 12px",
        borderRadius: 999,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 700,
    },
    pillOn: { background: "#111827", color: "#fff", border: "1px solid #111827" },
    pillOff: { opacity: 0.6 },

    primaryBtn: {
        border: "1px solid #2563eb",
        background: "#2563eb",
        color: "#fff",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 800,
    },
    secondaryBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        color: "#111827",
        padding: "10px 14px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 800,
    },

    muted: { marginTop: 10, color: "#6b7280", fontSize: 13 },

    viewBox: {
        marginTop: 10,
        border: "1px dashed #e5e7eb",
        borderRadius: 12,
        padding: 12,
        background: "#fafafa",
    },
    kv: {
        display: "grid",
        gridTemplateColumns: "140px 1fr",
        gap: 10,
        padding: "6px 0",
        borderBottom: "1px solid #eee",
    },
    k: { color: "#6b7280", fontWeight: 800, fontSize: 12 },
    v: { fontWeight: 700, fontSize: 13 },

    tableWrap: { overflowX: "auto" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
        textAlign: "left",
        fontSize: 12,
        color: "#6b7280",
        fontWeight: 900,
        padding: "10px 10px",
        borderBottom: "1px solid #e5e7eb",
        background: "#fcfcff",
    },
    td: {
        padding: "10px 10px",
        borderBottom: "1px solid #f1f5f9",
        fontSize: 14,
    },

    badge: {
        display: "inline-flex",
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        border: "1px solid transparent",
    },
    badgeOn: { background: "#ecfdf5", color: "#065f46", borderColor: "#a7f3d0" },
    badgeOff: { background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" },

    smallBtn: {
        border: "1px solid #e5e7eb",
        background: "#fff",
        padding: "7px 10px",
        borderRadius: 10,
        cursor: "pointer",
        fontWeight: 800,
        marginRight: 8,
    },
    dangerBtn: { borderColor: "#fecaca", color: "#991b1b", background: "#fff5f5" },

    modalOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(17,24,39,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
    },
    modal: {
        width: "min(520px, 100%)",
        background: "#fff",
        borderRadius: 14,
        border: "1px solid #e5e7eb",
        padding: 16,
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
    },
    modalTitle: { fontSize: 16, fontWeight: 900, marginBottom: 10 },
    modalActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 },

    // responsive
    "@media(max-width: 1100px)": undefined,
};

export default DepartmentPage;
